type PaysafeFields = { setup: (...args: unknown[]) => unknown };
type Paysafe = { fields?: PaysafeFields };
type PaysafeWindow = Window & { paysafe?: Paysafe };

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  typeof (value as PromiseLike<unknown> | null)?.then === 'function';

/**
 * @description Calls back with each Paysafe hosted fields instance the SDK sets up, which the SDK
 * keeps private, by wrapping paysafe.fields.setup. paysafe.min.js creates the global as an empty
 * object and adds fields after, so both assignments are trapped.
 * @returns a function that stops capturing and restores the global
 */
export const capturePaysafeInstance = (onInstance: (instance: unknown) => void) => {
  const paysafeWindow = window as PaysafeWindow;
  const wrappedFields = new WeakMap<PaysafeFields, PaysafeFields>();

  const wrapFields = (fields: PaysafeFields) => {
    const known = wrappedFields.get(fields);
    if (known) return known;
    // Paysafe's own properties are never written
    const wrapped: PaysafeFields = Object.create(fields, {
      setup: {
        value: (...args: unknown[]) => {
          const result = fields.setup(...args);
          if (isThenable(result)) {
            result.then(
              (instance) => {
                try {
                  onInstance(instance);
                } catch {
                  // Never break the SDK setup over validation styling
                }
              },
              () => {},
            );
          }
          return result;
        },
      },
    });
    wrappedFields.set(fields, wrapped);
    return wrapped;
  };

  const watchFields = (paysafe: Paysafe) => {
    let fields = paysafe.fields;
    Object.defineProperty(paysafe, 'fields', {
      configurable: true,
      enumerable: true,
      get: () => fields && wrapFields(fields),
      set: (next: PaysafeFields | undefined) => {
        fields = next;
      },
    });
    return () => {
      Object.defineProperty(paysafe, 'fields', {
        configurable: true,
        enumerable: true,
        writable: true,
        value: fields,
      });
    };
  };

  if (paysafeWindow.paysafe) return watchFields(paysafeWindow.paysafe);

  let paysafe: Paysafe | undefined;
  let stopWatchingFields: (() => void) | undefined;
  Object.defineProperty(paysafeWindow, 'paysafe', {
    configurable: true,
    enumerable: true,
    get: () => paysafe,
    set: (next: Paysafe | undefined) => {
      stopWatchingFields?.();
      paysafe = next;
      stopWatchingFields = next ? watchFields(next) : undefined;
    },
  });

  return () => {
    stopWatchingFields?.();
    delete paysafeWindow.paysafe;
    if (paysafe) paysafeWindow.paysafe = paysafe;
  };
};
